import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MultiSelect from "@/components/MultiSelect";
import { Mic, Square, Loader2, Send, Trash2, Briefcase, CheckCircle2, StickyNote, Pencil, X, Check } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export default function NotesPanel({ compact = false, ephemeral = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [text, setText] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [jobTags, setJobTags] = useState([]);
  const [posting, setPosting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [justSaved, setJustSaved] = useState(null);
  const [savedMsg, setSavedMsg] = useState(false);
  const [fading, setFading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editAssignees, setEditAssignees] = useState([]);
  const [editJobs, setEditJobs] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fadeTimers = useRef([]);

  const load = useCallback(() => {
    api.get("/notes").then((r) => setNotes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ephemeral) load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    return () => fadeTimers.current.forEach(clearTimeout);
  }, [load, ephemeral]);

  const userOptions = users.map((u) => ({ value: u.user_id, label: u.name || u.email, sub: u.email }));
  const jobOptions = jobs.map((j) => ({ value: j.id, label: j.title, sub: j.client_name || "" }));

  const addNote = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/notes", { text, assignee_ids: assignees, job_ids: jobTags });
      const saved = text;
      setText(""); setAssignees([]); setJobTags([]);
      if (ephemeral) {
        fadeTimers.current.forEach(clearTimeout);
        setSavedMsg(false); setFading(false); setJustSaved(saved);
        fadeTimers.current = [
          setTimeout(() => setFading(true), 4300),
          setTimeout(() => { setJustSaved(null); setSavedMsg(true); setFading(false); }, 5000),
        ];
      } else {
        load();
      }
    } catch { toast.error("Could not add note"); }
    finally { setPosting(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const r = await api.post("/voice/parse", fd);
          setText((t) => (t ? t + " " : "") + (r.data.transcript || ""));
        } catch { toast.error("Transcription failed"); }
        finally { setTranscribing(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const createJob = async (n) => {
    try { await api.patch(`/notes/${n.id}`, { converted_to_job: true }); }
    catch (error) { console.error("Failed to mark note as converted:", error); }
    navigate(`/jobs/new?notes=${encodeURIComponent(n.text)}`);
  };
  const remove = async (n) => {
    try { await api.delete(`/notes/${n.id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const canEdit = (n) => user && (n.created_by === user.user_id || user.role === "admin");
  const startEdit = (n) => {
    setEditId(n.id);
    setEditText(n.text);
    setEditAssignees((n.assignees || []).map((a) => a.user_id));
    setEditJobs((n.jobs || []).map((j) => j.job_id));
  };
  const cancelEdit = () => { setEditId(null); setEditText(""); setEditAssignees([]); setEditJobs([]); };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await api.patch(`/notes/${id}`, { text: editText, assignee_ids: editAssignees, job_ids: editJobs });
      cancelEdit(); load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only the note creator can edit this note" : "Could not save note");
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="notes-panel">
      {/* Composer */}
      <div className="border border-slate-200 rounded-xl p-3 bg-white">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 2 : 3}
          placeholder="Jot a note, or tap the mic to dictate…" data-testid="note-text-input" className="resize-none" />
        {!compact && (
          <div className="mt-2 space-y-2">
            <MultiSelect options={userOptions} value={assignees} onChange={setAssignees} placeholder="Assign to users…" testid="note-assignees" />
            <MultiSelect options={jobOptions} value={jobTags} onChange={setJobTags} placeholder="Tag to jobs…" testid="note-jobtags" />
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          {recording ? (
            <Button size="sm" variant="outline" onClick={stopRec} data-testid="note-stop-rec" className="text-red-600 border-red-200 animate-pulse"><Square className="h-4 w-4 mr-1.5" /> Stop</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={startRec} disabled={transcribing} data-testid="note-start-rec">
              {transcribing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mic className="h-4 w-4 mr-1.5" />} {transcribing ? "Transcribing…" : "Dictate"}
            </Button>
          )}
          <Button size="sm" onClick={addNote} disabled={posting || !text.trim()} data-testid="note-add-btn" className="ml-auto bg-slate-900 hover:bg-slate-800 text-white">
            {posting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Add note
          </Button>
        </div>
      </div>

      {/* List */}
      {ephemeral ? (
        <div className="mt-3" data-testid="notes-list">
          {justSaved && (
            <div className={`border border-amber-200 rounded-lg p-3 bg-amber-100 shadow-sm transition-opacity duration-700 ${fading ? "opacity-0" : "opacity-100"}`} data-testid="ephemeral-note">
              <p className="text-sm text-amber-950 whitespace-pre-line">{justSaved}</p>
              <p className="text-xs text-amber-700/70 mt-2">{user?.name || "You"} · just now</p>
            </div>
          )}
          {savedMsg && !justSaved && (
            <p className="text-sm text-emerald-700 py-6 text-center flex items-center justify-center gap-1.5" data-testid="note-saved-msg">
              <CheckCircle2 className="h-4 w-4" /> Saved to your notes page.
            </p>
          )}
        </div>
      ) : (
      <div className={`mt-3 space-y-2 ${compact ? "overflow-y-auto pr-1" : ""}`} style={compact ? { maxHeight: 360 } : undefined} data-testid="notes-list">
        {notes.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} data-testid={`note-${n.id}`} className="border border-amber-200 rounded-lg p-3 bg-amber-100 shadow-sm">
            {editId === n.id ? (
              <div className="space-y-2" data-testid={`note-edit-${n.id}`}>
                <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3}
                  className="resize-none bg-white" data-testid={`note-edit-text-${n.id}`} />
                <MultiSelect options={userOptions} value={editAssignees} onChange={setEditAssignees} placeholder="Assign to users…" testid={`note-edit-assignees-${n.id}`} />
                <MultiSelect options={jobOptions} value={editJobs} onChange={setEditJobs} placeholder="Tag to jobs…" testid={`note-edit-jobs-${n.id}`} />
                <div className="flex items-center gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={cancelEdit} data-testid={`note-edit-cancel-${n.id}`}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  <Button size="sm" onClick={() => saveEdit(n.id)} disabled={!editText.trim()} data-testid={`note-edit-save-${n.id}`} className="bg-slate-900 hover:bg-slate-800 text-white"><Check className="h-4 w-4 mr-1" /> Save</Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-amber-950 whitespace-pre-line">{n.text}</p>
                {(n.jobs || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {n.jobs.map((j) => (
                      <button key={j.job_id} onClick={() => navigate(`/jobs/${j.job_id}`)} data-testid={`note-jobtag-${j.job_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-medium rounded-full bg-slate-800 text-white px-1.5 py-0.5 hover:bg-slate-700">
                        <Briefcase className="h-2.5 w-2.5" /> {j.title}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center flex-wrap gap-1.5 mt-2">
                  <span className="text-xs text-amber-700/70">{n.created_by_name || "—"} · {timeAgo(n.created_at)}{n.updated_at ? " · edited" : ""}</span>
                  {(n.assignees || []).map((a) => (
                    <span key={a.user_id} className="text-[10px] rounded-full bg-lime-100 text-lime-800 px-1.5 py-0.5">{a.name}</span>
                  ))}
                  <div className="ml-auto flex items-center gap-1">
                    {n.converted_to_job ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Job created</span>
                    ) : (
                      <button onClick={() => createJob(n)} data-testid={`note-create-job-${n.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full border border-lime-300 bg-lime-50 text-lime-800 px-2 py-0.5 hover:bg-lime-100">
                        <Briefcase className="h-3 w-3" /> Create Job
                      </button>
                    )}
                    {canEdit(n) && (
                      <button onClick={() => startEdit(n)} data-testid={`note-edit-btn-${n.id}`} title="Edit note"
                        className="h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                    <button onClick={() => remove(n)} data-testid={`note-delete-${n.id}`} className="h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
